// /src/contexts/ThemeContext.tsx
import React, { createContext, useState, useMemo, useContext, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { getTheme } from '../theme';

interface IThemeContext {
  mode: 'light' | 'dark';
  toggleColorMode: () => void;
}

export const ThemeContext = createContext<IThemeContext>({
  mode: 'light',
  toggleColorMode: () => {},
});

export const AppThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    try {
      const storedMode = localStorage.getItem('themeMode');
      return storedMode === 'light' || storedMode === 'dark' ? storedMode : 'dark';
    } catch (error) {
      return 'dark';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('themeMode', mode);
      document.documentElement.setAttribute('data-theme', mode);
      document.body.setAttribute('data-theme', mode);
    } catch (error) {
      console.error('Could not save theme mode to localStorage', error);
    }
  }, [mode]);

  const toggleColorMode = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const theme = useMemo(() => createTheme(getTheme(mode)), [mode]);

  return (
    <ThemeContext.Provider value={{ mode, toggleColorMode }}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useAppTheme = () => useContext(ThemeContext);
